import React, { useMemo } from 'react'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import 'katex/contrib/mhchem'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

// Configure marked options for standard Github Flavored Markdown
marked.setOptions({
  gfm: true,
  breaks: false,
})

// Disable 4-space indented code blocks so indented HTML/SVG does not turn into <pre><code>
marked.use({
  tokenizer: {
    code() {
      return undefined
    },
  },
})

interface Props {
  content?: string
  text?: string
  format?: 'text' | 'latex' | 'markdown' | string
  className?: string
  inline?: boolean
  highlightOptions?: Array<string | { text?: string; content?: string }>
  disableDirectionCallout?: boolean
  disableExamHelpers?: boolean
}

// Sanitize configuration leveraging native DOMPurify profiles for HTML, SVG, SVG Filters, and MathML (KaTeX)
// Explicitly permit <use> with href / xlink:href for reusable SVG components (<defs>)
const DOMPURIFY_CONFIG = {
  USE_PROFILES: { html: true, svg: true, svgFilters: true, mathMl: true },
  ADD_TAGS: ['use'],
  ADD_ATTR: ['href', 'xlink:href'],
}

// Pre-processor to auto-detect and convert unformatted Column I / Column II match blocks into standard GFM tables
const HEADER_REGEX = /(?:^|\n)\s*(Column\s+(?:I|A|1)|List[- ]*(?:I|A|1)|Group\s+(?:I|A|1)|Set\s+(?:I|A|1))\s*[\t|,\s]+\s*(Column\s+(?:II|B|2)|List[- ]*(?:II|B|2)|Group\s+(?:II|B|2)|Set\s+(?:II|B|2))\s*(?=\n|$)/i
const COL1_START = /^\(?[A-Ea-e]\)?[.:]?\s+/
const COL2_START = /^\(?[1-9ivxIVX]+\)?[.:]?\s+/
const SINGLE_PAIR = /^(\(?[A-Ea-e]\)?[.:]?\s+[^(1-9)]+?)\s+(\(?[1-9ivxIVX]+\)?[.:]?\s+.+)$/

export function normalizeMatchColumns(text: string): string {
  if (!text || typeof text !== 'string') return text
  // If already formatted as a markdown table with separator row, leave untouched
  if (/\|[\s-:]+\|[\s-:]+\|/.test(text)) return text

  const match = text.match(HEADER_REGEX)
  if (!match || match.index == null) return text

  const headerCol1 = match[1].trim()
  const headerCol2 = match[2].trim()
  const headerStart = match.index + (match[0].startsWith('\n') ? 1 : 0)
  const headerEnd = match.index + match[0].length

  const prefix = text.slice(0, headerStart).trimEnd()
  const remaining = text.slice(headerEnd).trim()

  const lines = remaining.split('\n').map((l) => l.trim()).filter(Boolean)
  if (lines.length === 0) return text

  const rows: Array<{ col1: string; col2: string }> = []
  let currentCol1: string | null = null
  let currentCol2: string | null = null

  for (const line of lines) {
    const pair = line.match(SINGLE_PAIR)
    if (pair) {
      if (currentCol1 && currentCol2) {
        rows.push({ col1: currentCol1, col2: currentCol2 })
        currentCol1 = null
        currentCol2 = null
      }
      rows.push({ col1: pair[1].trim(), col2: pair[2].trim() })
      continue
    }

    if (COL1_START.test(line)) {
      if (currentCol1 && currentCol2) {
        rows.push({ col1: currentCol1, col2: currentCol2 })
        currentCol1 = null
        currentCol2 = null
      }
      currentCol1 = line
    } else if (COL2_START.test(line)) {
      currentCol2 = line
    } else if (currentCol2) {
      currentCol2 += ' ' + line
    } else if (currentCol1) {
      currentCol1 += ' ' + line
    }
  }

  if (currentCol1 && currentCol2) {
    rows.push({ col1: currentCol1, col2: currentCol2 })
  }

  if (rows.length === 0) return text

  const tableMd = [
    `| ${headerCol1} | ${headerCol2} |`,
    '| :--- | :--- |',
    ...rows.map((r) => `| ${r.col1.replace(/\|/g, '\\|')} | ${r.col2.replace(/\|/g, '\\|')} |`)
  ].join('\n')

  return prefix ? `${prefix}\n\n${tableMd}` : tableMd
}

/**
 * Converts ASCII box/grid representations (e.g. "+---+---+", "| 1 | 2 |", etc.)
 * commonly used for seating arrangement & floor puzzles into clean GFM tables.
 */
export function convertAsciiGridToMarkdownTable(text: string): string {
  if (!text || typeof text !== 'string') return text
  // Check if text has ASCII grid border lines like "+---+---+" or "+===+===+"
  if (!/\+[-=]+(?:\+[-=]+)+\+/.test(text)) return text

  const lines = text.split('\n')
  const processedLines: string[] = []
  let inAsciiGrid = false
  let tableRows: string[][] = []

  const flushTable = () => {
    if (tableRows.length > 0) {
      // First row becomes header, subsequent become body
      const headerRow = tableRows[0]
      const separatorRow = headerRow.map(() => ':---:')
      const formattedTable: string[] = [
        `| ${headerRow.join(' | ')} |`,
        `| ${separatorRow.join(' | ')} |`,
        ...tableRows.slice(1).map((r) => `| ${r.join(' | ')} |`),
      ]
      processedLines.push('\n' + formattedTable.join('\n') + '\n')
      tableRows = []
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()

    // Border row like "+----------+---+---+...+"
    if (/^\+[-=]+(?:\+[-=]+)+\+$/.test(trimmed)) {
      inAsciiGrid = true
      continue
    }

    // Content row like "| Position | 1 | 2 |...|"
    if (inAsciiGrid && trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const cells = trimmed
        .slice(1, -1)
        .split('|')
        .map((c) => c.trim())
      tableRows.push(cells)
      continue
    }

    // Non-grid line reached while inside grid
    if (inAsciiGrid) {
      flushTable()
      inAsciiGrid = false
    }

    processedLines.push(line)
  }

  flushTable()
  return processedLines.join('\n')
}

/**
 * Detects ASCII and Unicode diagrams (Euler / Venn models, flowcharts, tree structures,
 * seating arrangements, box-drawing characters) and preserves them in a clean, responsive,
 * fixed-width monospace canvas before markdown processing.
 */
export function normalizeAsciiDiagrams(text: string): string {
  if (!text || typeof text !== 'string') return text
  // If already enclosed in code block, pre tag, or contains SVG/HTML root, leave untouched
  if (text.includes('```') || text.includes('<pre') || text.includes('<svg') || text.trim().startsWith('<div')) return text

  // Strong diagram characters: Unicode box-drawing (U+2500 - U+257F), connectors, arrows
  const isStrongDiagramLine = (line: string): boolean => {
    const trimmed = line.trim()
    if (trimmed.startsWith('<') || trimmed.endsWith('>')) return false
    return /[\u2500-\u257F]|──►|◄──|───|(?<![a-zA-Z0-9])[▲▼◄►]|\+[-=]{2,}\+/.test(line)
  }

  const lines = text.split('\n')
  const result: string[] = []
  let diagramBuffer: string[] = []
  let nonDiagramBuffer: string[] = []

  const flushDiagram = () => {
    if (diagramBuffer.length > 0) {
      while (diagramBuffer.length > 0 && !diagramBuffer[0].trim()) diagramBuffer.shift()
      while (diagramBuffer.length > 0 && !diagramBuffer[diagramBuffer.length - 1].trim()) diagramBuffer.pop()

      if (diagramBuffer.length >= 2 && diagramBuffer.some(isStrongDiagramLine)) {
        // Auto-align vertical borders within rectangular diagram boxes
        let activeBoxWidth = 0

        const alignedLines = diagramBuffer.map((rawLine) => {
          const trimmed = rawLine.trimEnd()

          // Check for top border: ┌───┐, ╔═══╗, +---+
          if (/^[┌╔][─═]+[┐╗]$/.test(trimmed) || /^\+[-=]+\+$/.test(trimmed)) {
            activeBoxWidth = trimmed.length
            return trimmed
          }

          // Check for bottom border: └───┘, ╚═══╝, +---+
          if (/^[└╚][─═]+[┘╝]$/.test(trimmed)) {
            const targetW = activeBoxWidth || trimmed.length
            activeBoxWidth = 0
            if (targetW > 0 && trimmed.length !== targetW) {
              const startChar = trimmed[0]
              const endChar = trimmed.slice(-1)
              const fillChar = trimmed[1] || '─'
              return startChar + fillChar.repeat(Math.max(0, targetW - 2)) + endChar
            }
            return trimmed
          }

          // Reset activeBoxWidth if we hit non-diagram text
          if (activeBoxWidth > 0 && trimmed && !/^[│║├╠└╚+┌╔|]/.test(trimmed)) {
            activeBoxWidth = 0
          }

          // If inside an active box with a known target width:
          if (activeBoxWidth > 0) {
            // Horizontal divider like ├──────┤ or ╠══════╣
            if (/^[├╠]/.test(trimmed) && /[┤╣│║]$/.test(trimmed)) {
              const diff = activeBoxWidth - trimmed.length
              if (diff > 0) {
                const lastChar = trimmed.slice(-1)
                const fillChar = trimmed[1] || '─'
                return trimmed.slice(0, -1) + fillChar.repeat(diff) + lastChar
              } else if (diff < 0) {
                const lastChar = trimmed.slice(-1)
                const inner = trimmed.slice(0, -1)
                if (inner.endsWith(trimmed[1] || '─')) {
                  return inner.slice(0, diff) + lastChar
                }
              }
              return trimmed
            }

            // Content row starting with vertical border and ending with vertical border: e.g. │ ... │ or ║ ... ║ or | ... |
            if (/^[│║|]/.test(trimmed) && /[│║|]$/.test(trimmed)) {
              const diff = activeBoxWidth - trimmed.length
              if (diff > 0) {
                // Line is short: pad spaces before the closing vertical border
                const lastChar = trimmed.slice(-1)
                const inner = trimmed.slice(0, -1)
                return inner + ' '.repeat(diff) + lastChar
              } else if (diff < 0) {
                // Line is long: trim extra spaces before the closing vertical border if available
                const lastChar = trimmed.slice(-1)
                const inner = trimmed.slice(0, -1)
                const spaceMatch = inner.match(/(\s+)$/)
                if (spaceMatch && spaceMatch[1].length >= Math.abs(diff)) {
                  return inner.slice(0, diff) + lastChar
                }
              }
            }
          }

          return rawLine
        })

        // Escape HTML entities (<, >, &) and wrap wide Unicode glyphs/emojis in 1ch containers
        const formattedLines = alignedLines.map((line) => {
          const escaped = line
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')

          return escaped.replace(
            /([✔✘✖✕✓⟹⟺⟸★☆✦✧])/g,
            '<span class="diagram-glyph">$1</span>'
          )
        })

        result.push(
          '\n\n<div class="diagram-canvas my-3.5 overflow-x-auto rounded-xl border border-border/80 bg-muted/40 p-3.5 sm:p-4 text-foreground shadow-2xs"><pre class="font-mono text-xs sm:text-[13px] leading-snug whitespace-pre m-0 p-0 select-text">' +
            formattedLines.join('\n') +
            '</pre></div>\n\n'
        )
      } else {
        result.push(...diagramBuffer)
      }
      diagramBuffer = []
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const isStrong = isStrongDiagramLine(line)

    if (isStrong) {
      if (nonDiagramBuffer.length > 0) {
        if (diagramBuffer.length > 0 && nonDiagramBuffer.length <= 3) {
          diagramBuffer.push(...nonDiagramBuffer)
        } else {
          flushDiagram()
          result.push(...nonDiagramBuffer)
        }
        nonDiagramBuffer = []
      }
      diagramBuffer.push(line)
    } else if (diagramBuffer.length > 0) {
      // Line within or following diagram block
      const isIndented = /^\s{4,}/.test(line)
      const isDiagramItem = /^\s*\[.+\]\s*$/.test(line) || /^\s*•/.test(line) || line.includes('│') || line.includes('|')
      if (!line.trim() || isIndented || isDiagramItem) {
        nonDiagramBuffer.push(line)
      } else {
        flushDiagram()
        result.push(...nonDiagramBuffer)
        nonDiagramBuffer = []
        result.push(line)
      }
    } else {
      result.push(line)
    }
  }

  if (nonDiagramBuffer.length > 0 && diagramBuffer.length > 0) {
    diagramBuffer.push(...nonDiagramBuffer)
    nonDiagramBuffer = []
  }
  flushDiagram()
  if (nonDiagramBuffer.length > 0) {
    result.push(...nonDiagramBuffer)
  }

  return result.join('\n')
}

// Normalizes contextual word usage questions (e.g. Keyword at top, followed by sentences A, B, C, D, E)
// and multi-statement questions (e.g. A. ... B. ... C. ... or (I), (II), Statement I/II)
export function normalizeStatements(text: string): string {
  if (!text || typeof text !== 'string') return text
  let processed = text.trim()

  // 1. Detect if the text starts with a single keyword header (e.g. "Effective\n\n..." or "Word: Effective\n\n...")
  const keywordMatch = processed.match(/^(?:Word\s*[:\-]\s*)?([A-Za-z]{3,25})\s*\n{2,}([\s\S]+)$/i)
  let keyword: string | null = null
  let body = processed

  if (keywordMatch && !keywordMatch[1].includes(' ')) {
    keyword = keywordMatch[1].trim()
    body = keywordMatch[2].trim()
  }

  // 2. Check if body contains inline lettered sentences e.g. "A. ... B. ... C. ... D. ... E. ..."
  const letterMatches = body.match(/(?:^|(?<=[.?!:"])\s+|\n\s*)([A-Ea-e1-5])\.\s+/g)
  if (letterMatches && letterMatches.length >= 2) {
    body = body.replace(/(?:^|(?<=[.?!:"])\s+|\n\s*)([A-Ea-e1-5])\.\s+/g, (m, label, offset) => {
      const prefix = offset === 0 ? '' : '\n\n'
      return `${prefix}**${label.toUpperCase()}.** `
    })
  }

  // 3. Multi-statement patterns like (I), (II), (III) or Statement 1 / Assertion / Reason
  const statementRegex = /(?:^|\n)\s*(\((?:[IVXLCDMivxlcdm]+|[A-Ea-e]|\d+)\)|(?:Statement|Assertion|Reason|Conclusion|Premise)\s*(?:\([A-Za-z0-9]+\)|[IVXLCDMivxlcdm\d]+|[A-Za-z])?:?)\s+/g
  const matches = body.match(statementRegex)
  if (matches && matches.length >= 2) {
    body = body.replace(statementRegex, (match, label, offset) => {
      const cleanLabel = label.trim()
      const formattedLabel = cleanLabel.startsWith('**') ? cleanLabel : `**${cleanLabel}**`
      const prefix = offset === 0 ? '' : '\n\n'
      return `${prefix}${formattedLabel} `
    })
  }

  // 3b. Syllogism & Logic: Break inline numbered conclusions like "Conclusions: I. ... II. ..." into line breaks
  if (/\bConclusions?:\s*(?:[IVXLCDMivxlcdm\d]+[.:\)]\s+)/i.test(body) || body.includes('II.')) {
    body = body.replace(/(?:\b|\*\*)Conclusions?:\s*\*{0,2}/i, '\n\n**Conclusions:**\n\n')
    body = body.replace(/(?:^|\s+)([IVXLCDMivxlcdm]+|\d+)[.:\)]\s+(?=[A-Z])/g, '\n\n* **$1.** ')
  }

  // 3c. Syllogism statements: If "Statements:" contains multiple sentences, separate them into bullet items
  if (/\bStatements?:\s*(?:[A-Z])/i.test(body)) {
    body = body.replace(/(?:\b|\*\*)Statements?:\s*\*{0,2}/i, '\n\n**Statements:**\n\n')
    // Split sentences inside statements section if consecutive sentences like "Only a few... Some Lions... All dogs..."
    body = body.replace(/(\n\n\*\*Statements:\*\*\n\n)([\s\S]+?)(?=\n\n\*\*Conclusions:|\n{2,}|$)/i, (m, header, content) => {
      const sentences = content
        .split(/(?<=[.?!])\s+(?=[A-Z])/)
        .map((s) => s.trim())
        .filter(Boolean)
      if (sentences.length >= 2) {
        return `${header}* ${sentences.join('\n* ')}`
      }
      return m
    })
  }

  // 4. If keyword detected, auto-highlight the word within the sentences
  if (keyword) {
    const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(?<!\\*\\*|<strong>|<b>|<u>|<mark>)\\b(${escaped})\\b(?!\\*\\*|<\\/strong>|<\\/b>|<\\/u>|<\\/mark>)`, 'gi')
    body = body.replace(regex, '<strong class="highlight-tested-word">$1</strong>')

    return `<div class="word-usage-header"><span class="word-usage-badge">WORD UNDER TEST</span> <span class="word-usage-title">${keyword}</span></div>\n\n${body.trim()}`
  }

  return body.trim()
}

// Normalizes explanation text, bullet points (•, ▪, ⁃), and step-by-step headings for high readability
export function normalizeExplanationFormatting(text: string): string {
  if (!text || typeof text !== 'string') return text

  let processed = text

  // 1. Separate major solution headings into clean markdown section breaks
  processed = processed.replace(
    /(?:^|\s*)(Detailed Solution & Explanation|Detailed Explanation & Solution|Detailed Solution|Detailed Explanation|Solution & Explanation):/gi,
    '\n\n### Detailed Solution & Explanation\n\n'
  )

  processed = processed.replace(
    /(?:^|\s*)(Correct Sequence|Final Sequence|Key Takeaway|Key Concept|Formula Used|Chronology|Logical connections|Transitional cues|Conclusion):/gi,
    '\n\n**$1:** '
  )

  // 2. Convert bullet points (•, ▪, ⁃) into standard markdown list items with proper newlines
  processed = processed.replace(/(?:^|\n|(?<=[.?!:"])\s+)[•▪⁃]\s+/g, '\n\n* ')

  // 3. Convert semicolon-separated candidate/pair verification cases (e.g. "for (1, 3)...; for (2, 4)...; for (6, 7)...") into clean bullet points
  if (/(?:for\s*\([^)]+\)[^;]+;\s*){2,}/i.test(processed)) {
    processed = processed.replace(/:\s*(?=for\s*\([^)]+\))/i, ':\n\n* ')
    processed = processed.replace(/;\s*(?=for\s*\([^)]+\))/gi, '\n* ')
    processed = processed.replace(/;\s*(?=[A-Z][a-z]+)/g, '.\n\n')
  }

  // 4. Ensure numbered points like "1. Sentence (D)", "2. Step 2", etc. start on their own lines
  processed = processed.replace(/(?:^|\s+)(\d+)\.\s+(Sentence\s+\([A-Za-z0-9]+\)|Step\s+\d+|Option\s+\([A-Za-z0-9]+\)|Case\s+\d+)/g, '\n\n$1. **$2**')

  return processed.trim()
}

// Extracts direction, passage, and prompt text from question text if directions are embedded
export function parseQuestionDirectionAndPrompt(rawText: string): {
  direction: string | null
  passage: string | null
  prompt: string
  contextType: string
} {
  if (!rawText || typeof rawText !== 'string') {
    return { direction: null, passage: null, prompt: rawText || '', contextType: 'Directions' }
  }

  // 1. Unescape literal string representations of newlines that can come from JSON import
  let text = rawText
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n')
    .trim()

  // 2. Clean scraping artifacts
  text = text.replace(/\s*(?:https?:\/\/\S+|www\.[a-z0-9-]+\.[a-z]{2,}(?:\/\S*)?)\s*$/gi, '')

  let dirText: string | null = null
  let passageText: string | null = null
  let promptText = text
  let contextType = 'Directions'

  // Pattern 1: Cloze test / Passage with blanks / Reading Comprehension
  const clozeOrPassageMatch = text.match(
    /^(A passage is given with[^\n.]*\.[^\n.]*\.[^\n.]*\.|Read the passage[^\n.]*\.|In the following passage[^\n.]*\.|The following passage[^\n.]*\.|In the following passage, some words have been deleted[^\n.]*\.)\s*([\s\S]+)$/i
  )
  if (clozeOrPassageMatch) {
    dirText = clozeOrPassageMatch[1].trim()
    contextType = /blank|deleted|cloze/i.test(dirText) ? 'Cloze Test' : 'Reading Comprehension'
    const remaining = clozeOrPassageMatch[2].trim()
    const paras = remaining.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
    if (paras.length >= 2 && paras[paras.length - 1].endsWith('?')) {
      passageText = paras.slice(0, -1).join('\n\n').trim()
      promptText = paras[paras.length - 1].trim()
    } else {
      passageText = remaining
      promptText = 'Choose the correct option.'
    }
    return { direction: dirText, passage: passageText, prompt: promptText, contextType }
  }

  // Pattern 2: Explicit Direction: or Directions: or Directions (Q. 1-5):
  const explicitDirMatch = text.match(/^(?:Directions?|Instructions?)(?:\s*\([^)]+\))?\s*[:.-]\s*/i)
  if (explicitDirMatch) {
    const afterPrefix = text.slice(explicitDirMatch[0].length).trim()

    // Check for reading passage e.g. "Read the passage and answer the questions: <Passage> \n\n <Prompt>"
    const rcMatch = afterPrefix.match(/^(Read the passage and answer the questions?:?)\s*([\s\S]+)$/i)
    if (rcMatch) {
      contextType = 'Reading Comprehension'
      dirText = rcMatch[1].trim()
      const remaining = rcMatch[2].trim()
      const paras = remaining.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
      if (paras.length >= 2 && paras[paras.length - 1].endsWith('?')) {
        passageText = paras.slice(0, -1).join('\n\n').trim()
        promptText = paras[paras.length - 1].trim()
      } else {
        passageText = remaining
        promptText = 'Answer the question based on the passage above.'
      }
      return { direction: dirText, passage: passageText, prompt: promptText, contextType }
    }

    // Check for error spotting pills / slashes in the text
    const slashIdx = afterPrefix.search(/\([A-Ea-e1-5]\)\s*[\/]|[\/]\s*\([A-Ea-e1-5]\)/)
    if (slashIdx !== -1) {
      const beforeSlash = afterPrefix.slice(0, slashIdx)
      const lastSentenceMatch = beforeSlash.match(/([\s\S]*[.?!'’"])\s+(?=[A-Z0-9])/i)
      if (lastSentenceMatch) {
        dirText = lastSentenceMatch[1].trim()
        promptText = afterPrefix.slice(lastSentenceMatch[1].length).trim()
      } else {
        const doubleNlIdx = beforeSlash.lastIndexOf('\n\n')
        if (doubleNlIdx !== -1) {
          dirText = afterPrefix.slice(0, doubleNlIdx).trim()
          promptText = afterPrefix.slice(doubleNlIdx + 2).trim()
        }
      }
    } else {
      const paras = afterPrefix.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
      if (paras.length >= 3 && paras[paras.length - 1].endsWith('?')) {
        dirText = paras[0].trim()
        passageText = paras.slice(1, -1).join('\n\n').trim()
        promptText = paras[paras.length - 1].trim()
        contextType = /passage/i.test(dirText) ? 'Reading Comprehension' : 'Context'
      } else if (paras.length >= 2) {
        dirText = paras[0].trim()
        promptText = paras.slice(1).join('\n\n').trim()
      }
    }
  } else {
    // Pattern 3: Implicit directions
    const paras = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
    if (paras.length >= 3 && paras[paras.length - 1].endsWith('?') && /passage|blanks|cloze|information/i.test(paras[0])) {
      dirText = paras[0]
      passageText = paras.slice(1, -1).join('\n\n')
      promptText = paras[paras.length - 1]
      contextType = /blank|cloze/i.test(dirText) ? 'Cloze Test' : 'Reading Comprehension'
    } else if (paras.length >= 2) {
      const firstPara = paras[0].trim()
      if (
        /^(?:Read each sentence|In the following|In each of the|In each question|Study the following|Read the following|Read the passage)/i.test(
          firstPara
        )
      ) {
        dirText = firstPara
        promptText = paras.slice(1).join('\n\n').trim()
        if (/passage/i.test(firstPara)) contextType = 'Reading Comprehension'
        else if (/statements|conclusions/i.test(firstPara)) contextType = 'Logical Reasoning'
      }
    }
  }

  return { direction: dirText, passage: passageText, prompt: promptText, contextType }
}

// Formats bold tested words with part markers for Word Swap / Misspelt Words and Cloze Test blanks
// e.g. "**engineer (A)**", "**engineer** (A)", "**____(A)____**" -> styled words & pill badges
export function formatWordSwapSentence(text: string): string {
  if (!text || typeof text !== 'string') return text
  let processed = text

  // Cloze test blanks: **____(A)____** or **____ (A) / ____** or ____(A)____ or ________(A)
  processed = processed.replace(
    /(?:\*\*)?_{2,}\s*[\(\[（]([A-Ea-e1-5])[\)\]）](?:\s*[\/]?\s*_{2,})?(?:\*\*)?/g,
    '&lowbar;&lowbar;&lowbar;&lowbar;&nbsp;<span class="sentence-part-pill">($1)</span>&nbsp;&lowbar;&lowbar;&lowbar;&lowbar;'
  )

  // Case 1: **word (A)** or **word (A) / ** or **word [A]**
  processed = processed.replace(
    /\*\*([^\n*]{1,80}?)\s*[\(\[（]([A-Ea-e1-5])[\)\]）]\s*[\/]?\s*\*\*/g,
    '<strong class="font-bold">$1</strong>&nbsp;<span class="sentence-part-pill">($2)</span>'
  )

  // Case 2: **word** (A) or **word** [A]
  processed = processed.replace(
    /\*\*([^\n*]{1,80}?)\*\*\s*[\(\[（]([A-Ea-e1-5])[\)\]）]/g,
    '<strong class="font-bold">$1</strong>&nbsp;<span class="sentence-part-pill">($2)</span>'
  )

  return processed
}

// Formats error spotting sentences as a natural continuous sentence with inline pill badges and clean slashes
export function formatErrorSpottingSentence(text: string): string {
  if (!text || typeof text !== 'string') return text
  let processed = text.trim()

  // Clean trailing dots, slashes, or scraping artifacts
  processed = processed.replace(/\s*(?:https?:\/\/\S+|www\.[a-z0-9-]+\.[a-z]{2,}(?:\/\S*)?)\s*$/gi, '')

  // Check if text has error spotting slash/pill patterns: (A), (B), (C), (D), (E), or No Error
  const hasPartMarkers = /[\(（][A-Ea-e1-5][\)）]/.test(processed)
  const isErrorSpotting =
    /\([A-Ea-e1-5]\)\s*[\/]|[\/]\s*[\(（]([A-Ea-e1-5])[\)）]|\b(No [Ee]rror)\s*[\/]?\s*[\(（]([A-Ea-e1-5])[\)）]/i.test(processed) ||
    (/\b(No\s*Error)\b/i.test(processed) && hasPartMarkers)

  if (!isErrorSpotting) return processed

  // Normalize all variations into standard format with slashes:
  // e.g. "goals(D)/" -> "goals (D)/"
  processed = processed.replace(/([^\s\/])\s*[\(（]([A-Ea-e1-5])[\)）]/g, '$1 ($2)')
  processed = processed.replace(/\s*[\/]\s*[\(（]([A-Ea-e1-5])[\)）]\s*/g, ' ($1)/ ')
  processed = processed.replace(/\b(No [Ee]rror)\s*[\/]?\s*[\(（]([A-Ea-e1-5])[\)）]/gi, 'No Error ($2)')
  // If consecutive part markers are missing slashes (e.g. "...intelligent (A) as well as..."), insert a slash delimiter after each (A), (B), (C), (D)
  processed = processed.replace(/[\(（]([A-Ea-e1-5])[\)）](?!\s*\/)(?!\s*$)/g, '($1) /')

  // Clean trailing slashes and periods at the end of the full sentence
  processed = processed.replace(/[\/.\s]+$/, '')

  // Split by slashes
  const rawSegments = processed.split(/\s*[\/]\s*/).map((s) => s.trim()).filter(Boolean)
  if (rawSegments.length < 2) return processed

  const formattedSegments = rawSegments.map((seg) => {
    let s = seg.trim()
    const labelMatch = s.match(/[\(（]([A-Ea-e1-5])[\)）]/)
    if (labelMatch) {
      const label = labelMatch[1].toUpperCase()
      let textWithoutLabel = s.replace(/[\(（][A-Ea-e1-5][\)）]/g, '').trim()
      textWithoutLabel = textWithoutLabel.replace(/\s*\.\s*$/, '')
      return `${textWithoutLabel}&nbsp;<span class="sentence-part-pill">(${label})</span>`
    }
    return s
  })

  return `<div class="error-spotting-sentence-flow">${formattedSegments.join(' <span class="sentence-part-slash">/</span> ')}.</div>`
}

// Normalizes question directions, error-spotting sentence parts, reading passages, and question prompts
export function normalizeQuestionDirectionsAndSentences(text: string): string {
  if (!text || typeof text !== 'string') return text

  let processed = text.trim()

  // 0. Remove scraping artifacts like www.prepp.in, www.testbook.com, etc.
  processed = processed.replace(/\s*(?:https?:\/\/\S+|www\.[a-z0-9-]+\.[a-z]{2,}(?:\/\S*)?)\s*$/gi, '')

  // 1. Identify if text contains a Direction / Instruction
  let dirText: string | null = null
  let remainingText = processed

  const explicitDirMatch = processed.match(/^(?:Directions?|Instructions?)(?:\s*\([^)]+\))?\s*[:.-]\s*/i)
  if (explicitDirMatch) {
    const afterPrefix = processed.slice(explicitDirMatch[0].length).trim()
    const slashIdx = afterPrefix.search(/\([A-Ea-e1-5]\)\s*[\/]|[\/]\s*[\(（]([A-Ea-e1-5])[\)）]/)

    if (slashIdx !== -1) {
      // There are error-spotting parts! Find where the question sentence starts
      const beforeSlash = afterPrefix.slice(0, slashIdx)
      const lastSentenceMatch = beforeSlash.match(/([\s\S]*[.?!'’"])\s+(?=[A-Z0-9])/i)
      if (lastSentenceMatch) {
        dirText = lastSentenceMatch[1].trim()
        remainingText = afterPrefix.slice(lastSentenceMatch[1].length).trim()
      } else {
        const doubleNlIdx = beforeSlash.lastIndexOf('\n\n')
        if (doubleNlIdx !== -1) {
          dirText = afterPrefix.slice(0, doubleNlIdx).trim()
          remainingText = afterPrefix.slice(doubleNlIdx + 2).trim()
        }
      }
    } else {
      // Check if it's Reading Comprehension like "Read the passage and answer the questions: ... \n\n<Question>"
      const rcMatch = afterPrefix.match(/^(Read the passage and answer the questions?:?)\s*([\s\S]+)$/i)
      if (rcMatch) {
        dirText = rcMatch[1].trim()
        remainingText = rcMatch[2].trim()
      } else {
        const paras = afterPrefix.split(/\n{2,}/)
        if (paras.length >= 2) {
          dirText = paras[0].trim()
          remainingText = paras.slice(1).join('\n\n').trim()
        }
      }
    }
  } else {
    // Check implicit direction
    const paras = processed.split(/\n{2,}/)
    if (paras.length >= 2) {
      const firstPara = paras[0].trim()
      if (
        /^(?:Read each sentence|In the following|In each of the|In each question|Study the following|Read the following|Read the passage)/i.test(
          firstPara
        )
      ) {
        const remaining = paras.slice(1).join('\n\n').trim()
        const hasQuestionStructure =
          remaining.includes('?') ||
          remaining.includes('<span class="sentence-part-pill">') ||
          /\([A-Ea-e1-5]\)\s*[\/]/.test(remaining) ||
          /^(?:Which|What|How|Find|Select|Identify|Choose|Where|Who|When)\b/i.test(remaining)

        if (hasQuestionStructure) {
          dirText = firstPara
          remainingText = remaining
        }
      }
    }
  }

  // Check if remainingText has a reading passage + final question
  if (remainingText && dirText && /passage|information/i.test(dirText)) {
    const paras = remainingText.split(/\n{2,}/)
    if (paras.length >= 2 && paras[paras.length - 1].trim().endsWith('?')) {
      const passageBody = paras.slice(0, -1).join('\n\n').trim()
      const questionPrompt = paras[paras.length - 1].trim()
      remainingText = `<div class="question-passage-card">${passageBody}</div>\n\n<div class="question-prompt-highlight">${questionPrompt}</div>`
    }
  }

  // Format word-swap / misspelt bold markers if present
  remainingText = formatWordSwapSentence(remainingText)

  // Format error-spotting sentence if detected
  remainingText = formatErrorSpottingSentence(remainingText)

  if (dirText && remainingText) {
    return `<div class="question-direction-callout"><div class="direction-header"><span class="direction-tag">DIRECTIONS</span></div><div class="direction-body">${dirText}</div></div>\n\n${remainingText}`
  }

  return remainingText
}

// Unwraps single hard line-breaks that occur mid-sentence from fixed-width copy-pastes/extractions,
// while preserving paragraph breaks (\n\n), list items, headers, tables, blockquotes, and code blocks.
export function unwrapSoftLineBreaks(text: string): string {
  if (!text || typeof text !== 'string') return text

  // Split by double newlines into logical blocks (paragraphs, tables, lists)
  const blocks = text.split(/\n{2,}/)

  const processedBlocks = blocks.map((block) => {
    // If block is a table, code block, display math, diagram canvas, or html block, leave untouched
    if (
      block.includes('| --- |') ||
      block.includes('| :--- |') ||
      block.includes('$$') ||
      block.includes('\\[') ||
      block.startsWith('```') ||
      block.startsWith('<div') ||
      block.startsWith('<span') ||
      block.includes('diagram-canvas') ||
      block.includes('<pre') ||
      /[\u2500-\u257F]/.test(block) ||
      /──►|◄──|\+[-=]{2,}\+/.test(block)
    ) {
      return block
    }

    const lines = block.split('\n')
    const resultLines: string[] = []

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      const trimmed = line.trim()

      if (!trimmed) {
        resultLines.push('')
        continue
      }

      // Check if line is an intentional structural break (list item, header, blockquote, bullet, statement, display math, diagram line)
      const isStructural =
        /^(?:[*+•▪⁃-]\s+|\d+\.\s+|[#>]|\$\$|\\\[|\\\]|\|\s*|\*\*\([A-Za-z0-9IVXLCDMivxlcdm]+\)\*\*|\([A-Za-z0-9IVXLCDMivxlcdm]+\)|\*\*Statement|\*\*Assertion|\*\*Reason|\*\*Conclusion|\*\*Correct|\*\*Final|\*\*Key|\*\*Formula|\*\*Chronology)|[\u2500-\u257F]|──►|◄──|\+[-=]{2,}\+/i.test(
          trimmed
        )

      if (isStructural || resultLines.length === 0) {
        resultLines.push(line)
      } else {
        // Append to previous line with a space to heal the split sentence
        resultLines[resultLines.length - 1] = resultLines[resultLines.length - 1].trimEnd() + ' ' + trimmed
      }
    }

    return resultLines.join('\n')
  })

  return processedBlocks.join('\n\n')
}

// Automatically highlights tested words (e.g. for misspelt words, word swaps) if options match words in the sentence
export function autoHighlightTestedWords(
  text: string,
  options?: Array<string | { text?: string; content?: string }>
): string {
  if (!text || typeof text !== 'string' || !options || options.length === 0) return text

  const metaOptionRegex =
    /^(?:all\s+are\s+correct|none\s+of\s+these|none\s+of\s+the\s+above|all\s+of\s+the\s+above|no\s+error|both\s+[a-e\d]\s+and\s+[a-e\d]|either\s+[a-e\d]\s+or\s+[a-e\d]|cannot\s+be\s+determined|all\s+correct)$/i

  const candidateWords = options
    .map((opt) => (typeof opt === 'string' ? opt : opt?.text || opt?.content || ''))
    .map((w) => w.trim().replace(/^[\(（]?[A-Ea-e1-5][\)）]?[.:]?\s*/, '').trim())
    .filter((w) => w.length >= 3 && !w.includes(' ') && !metaOptionRegex.test(w))

  if (candidateWords.length < 2) return text

  let processed = text
  candidateWords.forEach((word) => {
    // Only highlight if not already inside a **...** or <strong... or <u>...
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(?<!\\*\\*|<strong>|<b>|<u>|<mark>)\\b(${escaped})\\b(?!\\*\\*|<\\/strong>|<\\/b>|<\\/u>|<\\/mark>)`, 'gi')
    processed = processed.replace(regex, '<strong class="highlight-tested-word">$1</strong>')
  })

  return processed
}

// Automatically normalizes raw Unicode math symbols (√, ∛, ∜, superscripts ², ³, subscripts, degrees °) into KaTeX LaTeX
export function normalizeUnicodeMath(text: string): string {
  if (!text || typeof text !== 'string') return text
  let processed = text

  // 0. Normalize unicode minus / en-dash / em-dash in math contexts (between numbers or parentheses)
  processed = processed.replace(/([0-9\?\)\s])[\u2013\u2014\u2212]([0-9\?\(\s])/g, '$1-$2')

  // 1. Unicode Roots:
  // Cube roots: ∛(x) or ∛27 or ∛{27} -> $\sqrt[3]{27}$
  processed = processed.replace(/∛(?:\{([^}]+)\}|\(([^)]+)\)|(\d+|\?|[a-zA-Z]+))/g, (m, c1, c2, c3) => `$\\sqrt[3]{${c1 || c2 || c3}}$`)
  // Fourth roots: ∜(x) or ∜16 or ∜{16} -> $\sqrt[4]{16}$
  processed = processed.replace(/∜(?:\{([^}]+)\}|\(([^)]+)\)|(\d+|\?|[a-zA-Z]+))/g, (m, c1, c2, c3) => `$\\sqrt[4]{${c1 || c2 || c3}}$`)
  // Square roots: √(x) or √256 or √{256} or √? or √{?} -> $\sqrt{...}$
  processed = processed.replace(/√(?:\{([^}]+)\}|\(([^)]+)\)|(\d+|\?|[a-zA-Z]+))/g, (m, c1, c2, c3) => `$\\sqrt{${c1 || c2 || c3}}$`)

  // 1b. Greek Letters: π, θ, α, β, λ, μ, σ, Δ
  processed = processed.replace(/(?<![a-zA-Z\\])π(?![a-zA-Z])/g, '$\\pi$')
  processed = processed.replace(/(?<![a-zA-Z\\])θ(?![a-zA-Z])/g, '$\\theta$')
  processed = processed.replace(/(?<![a-zA-Z\\])α(?![a-zA-Z])/g, '$\\alpha$')
  processed = processed.replace(/(?<![a-zA-Z\\])β(?![a-zA-Z])/g, '$\\beta$')
  processed = processed.replace(/(?<![a-zA-Z\\])λ(?![a-zA-Z])/g, '$\\lambda$')
  processed = processed.replace(/(?<![a-zA-Z\\])μ(?![a-zA-Z])/g, '$\\mu$')
  processed = processed.replace(/(?<![a-zA-Z\\])σ(?![a-zA-Z])/g, '$\\sigma$')
  processed = processed.replace(/(?<![a-zA-Z\\])Δ(?![a-zA-Z])/g, '$\\Delta$')

  // 1c. Unit area / volume abbreviations with superscripts: cm², m², mm², km², etc.
  processed = processed.replace(/\b(cm|m|mm|km|in|ft|s)\s*²\b/g, '$\\text{$1}^2$')
  processed = processed.replace(/\b(cm|m|mm|km|in|ft|s)\s*³\b/g, '$\\text{$1}^3$')

  // 2. Superscript numbers e.g. 18², 16², 5³, 10⁵, (x)², x²
  const supMap: Record<string, string> = {
    '⁰': '0', '¹': '1', '²': '2', '³': '3', '⁴': '4',
    '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9',
    '⁺': '+', '⁻': '-', 'ⁿ': 'n'
  }
  processed = processed.replace(/([a-zA-Z0-9\?\)\}\]]+)([⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻ⁿ]+)/g, (match, base, sups) => {
    const exponent = sups.split('').map((c: string) => supMap[c] || c).join('')
    return `$${base}^{${exponent}}$`
  })

  // 2b. Caret powers written in raw text without math delimiters (e.g. "20^2 = 400", "x^3", "(10)^2")
  processed = processed.replace(/(?<![\$\\\w])([a-zA-Z0-9]+|\([a-zA-Z0-9]+\))\^(\d+|[a-zA-Z]+)(?![\$\w])/g, '$$$1^{$2}$$')

  // 3. Subscript numbers e.g. H₂O, a₁, x₂
  const subMap: Record<string, string> = {
    '₀': '0', '₁': '1', '₂': '2', '₃': '3', '₄': '4',
    '₅': '5', '₆': '6', '₇': '7', '₈': '8', '₉': '9'
  }
  processed = processed.replace(/([a-zA-Z0-9\?\)\}\]]+)([₀₁₂₃₄₅₆₇₈₉]+)/g, (match, base, subs) => {
    const subscript = subs.split('').map((c: string) => subMap[c] || c).join('')
    return `$${base}_{${subscript}}$`
  })

  // 4. Mixed fractions like 5 1/2 or 3 3/4
  processed = processed.replace(/\b(\d+)\s+([1-9]\d*)\/([1-9]\d*)\b/g, '$$$1 \\frac{$2}{$3}$$')

  // 5. Degree symbol e.g. 45° -> $45^\circ$
  processed = processed.replace(/\b(\d+)\s*°/g, '$$$1^\\circ$$')

  // 6. Math comparison & relation symbols
  processed = processed.replace(/≠/g, '$\\neq$')
  processed = processed.replace(/≤/g, '$\\le$')
  processed = processed.replace(/≥/g, '$\\ge$')
  processed = processed.replace(/≈/g, '$\\approx$')
  processed = processed.replace(/±/g, '$\\pm$')

  // 7. Math operators between tokens (×, ÷)
  processed = processed.replace(/(?<=[0-9a-zA-Z\)\}\]\$])\s*×\s*(?=[0-9a-zA-Z\(\{\[\$])/g, ' $\\times$ ')
  processed = processed.replace(/(?<=[0-9a-zA-Z\)\}\]\$])\s*÷\s*(?=[0-9a-zA-Z\(\{\[\$])/g, ' $\\div$ ')

  // 8. Implication arrows (⟹, ⇒) in mathematical contexts
  processed = processed.replace(/(?<=[0-9a-zA-Z\)\}\]\$\s])[⟹⇒](?=[0-9a-zA-Z\(\{\[\$\s])/g, ' $\\implies$ ')

  // Clean empty inline math blocks (e.g., "$$" or "$ $" with only whitespace between single dollar signs)
  // Ensure we do NOT strip display math "$$...$$" delimiters by requiring boundaries or non-dollar context
  processed = processed.replace(/(?<!\$)\$\s*\$(?!\$)/g, '')

  return processed
}

export function KatexRenderer({
  content,
  text,
  format = 'markdown',
  className = '',
  inline = false,
  highlightOptions,
  disableDirectionCallout = false,
  disableExamHelpers = false,
}: Props) {
  const rawText = content ?? text ?? ''

  const renderedHtml = useMemo(() => {
    if (!rawText || !rawText.trim()) return ''

    // Helper to escape HTML if pure text with no formatting
    const escapeHtml = (str: string) =>
      str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

    const mathTokens: string[] = []

    // Helper to safely render and store LaTeX math token
    const tokenizeMath = (rawMath: string, isDisplay: boolean) => {
      try {
        const rendered = katex.renderToString(rawMath.trim(), { displayMode: isDisplay, throwOnError: false })
        const token = isDisplay ? `KATEXDISPTOKEN${mathTokens.length}END` : `KATEXINLINETOKEN${mathTokens.length}END`
        mathTokens.push(isDisplay ? `<div class="katex-display-wrapper my-3 overflow-x-auto text-center">${rendered}</div>` : rendered)
        return isDisplay ? (inline ? token : `\n\n${token}\n\n`) : token
      } catch {
        return escapeHtml(rawMath)
      }
    }

    // 0. FIRST: Protect all raw SVG blocks from any preprocessing and Markdown parsers
    const svgTokens: string[] = []
    let processed = rawText.replace(/<svg\b[\s\S]*?<\/svg>/gi, (match) => {
      svgTokens.push(match)
      return `PREPKIYASVGTOKEN${svgTokens.length - 1}END`
    })

    // Tokenize already valid display math ($$...$$ or \[...\]) and inline math ($...$ or \(...\))
    // to protect genuine LaTeX math (e.g. $\sqrt{(329 + 20^2)}$) from being broken by regex replacements.
    processed = processed.replace(/(\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\])/g, (match) => {
      const math = match.startsWith('$$') ? match.slice(2, -2) : match.slice(2, -2)
      return tokenizeMath(math, true)
    })

    processed = processed.replace(/(\$(?:\\\$|[^\$\n])+?\$|\\\(.*?\\\))/g, (match) => {
      const math = match.startsWith('$') ? match.slice(1, -1) : match.slice(2, -2)
      return tokenizeMath(math, false)
    })

    // 1. SECOND: Preprocess remaining raw text Unicode math symbols (√, ², ³, °, etc.) and ASCII caret powers (20^2)
    processed = normalizeUnicodeMath(processed)

    // 1b. Tokenize newly generated inline math ($...$ or \(...\)) from normalizeUnicodeMath
    processed = processed.replace(/(\$(?:\\\$|[^\$\n])+?\$|\\\(.*?\\\))/g, (match) => {
      const math = match.startsWith('$') ? match.slice(1, -1) : match.slice(2, -2)
      return tokenizeMath(math, false)
    })

    // 2.5. Defensive recovery: Detect un-delimited LaTeX formulas/commands (e.g. \text{...}, \frac{...}{...}, \times, \implies)
    // that were stored without $...$ or $$...$$ delimiters and tokenize them so they render cleanly.
    const NAKED_LATEX_COMMANDS = /\\(?:frac|text|times|implies|approx|le|ge|pm|neq|sqrt|div|cdot|alpha|beta|gamma|theta|pi|sigma|Delta|sum|int|infty|rightarrow|leftarrow|left|right)\b/

    const renderInlineNakedLatex = (str: string): string => {
      if (!str || !NAKED_LATEX_COMMANDS.test(str)) return str
      return str.replace(/(\\(?:frac\{[^{}]*\}\{[^{}]*\}|text\{[^{}]*\}|sqrt\{[^{}]*\}|times|implies|approx|le|ge|pm|neq|div|cdot|alpha|beta|gamma|theta|pi|sigma|Delta|sum|int|infty|rightarrow|leftarrow)\b)/g, (m) => {
        try {
          const rendered = katex.renderToString(m.trim(), { displayMode: false, throwOnError: false })
          const token = `KATEXINLINETOKEN${mathTokens.length}END`
          mathTokens.push(rendered)
          return token
        } catch {
          return m
        }
      })
    }

    const lines = processed.split('\n')
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      // Skip lines that are already fully tokenized or markdown tables / headers
      if (line.includes('KATEXDISPTOKEN') || line.startsWith('|') || line.startsWith('#')) continue

      if (NAKED_LATEX_COMMANDS.test(line)) {
        // If the line contains ordinary narrative prose words (words with 3+ letters not part of commands),
        // it is a text paragraph containing inline math, NEVER a whole-line display equation.
        const strippedOfCommands = line.replace(/\\(?:frac|text|times|implies|approx|le|ge|pm|neq|sqrt|div|cdot|alpha|beta|gamma|theta|pi|sigma|Delta|sum|int|infty|rightarrow|leftarrow|left|right)\b/g, '')
        const hasNarrativeWords = /\b[a-zA-Z]{3,}\b/.test(strippedOfCommands)

        if (!hasNarrativeWords && (/^\s*\\/.test(line) || /=\s*\\/.test(line) || /\b\d+[a-zA-Z]*\s*\\implies/.test(line)) && /[=+\-*\/^]|\b(?:frac|sqrt|times|implies)\b/.test(line)) {
          const rawMath = line.trim()
          try {
            const rendered = katex.renderToString(rawMath, { displayMode: true, throwOnError: false })
            const token = `KATEXDISPTOKEN${mathTokens.length}END`
            mathTokens.push(`<div class="katex-display-wrapper my-3 overflow-x-auto text-center">${rendered}</div>`)
            lines[i] = inline ? token : `\n\n${token}\n\n`
          } catch {
            lines[i] = renderInlineNakedLatex(line)
          }
        } else {
          lines[i] = renderInlineNakedLatex(line)
        }
      }
    }
    let preprocessedText = processed

    if (!disableExamHelpers) {
      // 3. Preprocess explanation formatting (bullets, step headers, sequence markers)
      preprocessedText = normalizeExplanationFormatting(preprocessedText)

      // 3. Preprocess text to catch unformatted tables/columns
      preprocessedText = normalizeMatchColumns(preprocessedText)

      // 4. Preprocess ASCII grids/boxes (puzzles, seating arrangement grids) into styled markdown tables
      preprocessedText = convertAsciiGridToMarkdownTable(preprocessedText)

      // 4b. Preprocess ASCII / Unicode diagrams (Euler/Venn models, flowcharts, seating box art) into monospace diagram canvases
      preprocessedText = normalizeAsciiDiagrams(preprocessedText)

      // 5. Preprocess statement lists (I, II, III / Statement 1, 2) if not already a table
      if (!preprocessedText.includes('| --- |') && !preprocessedText.includes('| :--- |')) {
        preprocessedText = normalizeStatements(preprocessedText)
      }

      // 6. Unwrap soft line-breaks on text sentences
      preprocessedText = unwrapSoftLineBreaks(preprocessedText)

      // 6. Preprocess directions, error spotting sentence parts, and question prompts (only in block mode)
      if (!inline && !disableDirectionCallout) {
        preprocessedText = normalizeQuestionDirectionsAndSentences(preprocessedText)
      } else {
        preprocessedText = formatWordSwapSentence(preprocessedText)
        if (!inline) {
          preprocessedText = formatErrorSpottingSentence(preprocessedText)
        }
      }

      // 7. Preprocess auto-highlighting of tested words from option choices
      if (highlightOptions && highlightOptions.length > 0) {
        preprocessedText = autoHighlightTestedWords(preprocessedText, highlightOptions)
      }
    }

    // 8. Convert alert callouts like [!IMPORTANT] or [!NOTE] or [!WARNING] into clean callout divs
    preprocessedText = preprocessedText.replace(
      /(?:^|\n)\s*\[!(IMPORTANT|NOTE|WARNING|TIP|CAUTION)\]\s*([\s\S]+?)(?=\n\s*\[!|\n\s*#|\n\s*---| \n\s*\d+\.|\n{2,}|$)/gi,
      (match, type, content) => {
        const t = type.toUpperCase()
        const badgeColor = t === 'IMPORTANT' || t === 'WARNING' ? 'border-primary/40 bg-primary/10 text-primary' : 'border-secondary/40 bg-secondary/10 text-secondary'
        return `\n\n<div class="my-3 p-3.5 rounded-xl border ${badgeColor} text-xs leading-relaxed"><strong class="font-bold mr-1.5 uppercase text-[10px] tracking-wider px-1.5 py-0.5 rounded bg-card/80 inline-block">${t}</strong>${content.trim()}</div>\n\n`
      }
    )


    // If strictly 'text' format and no LaTeX/Markdown syntax is found, escape and return linebreaks
    if (
      format === 'text' &&
      mathTokens.length === 0 &&
      !/[#*`_~>\[\]|<>]/.test(preprocessedText)
    ) {
      const escaped = escapeHtml(preprocessedText).replace(/\n\n+/g, '<br /><br />').replace(/\n/g, ' ')
      return escaped
    }

    // 9. Parse Markdown with marked
    let html = ''
    try {
      if (inline) {
        html = marked.parseInline(preprocessedText) as string
      } else {
        html = marked.parse(preprocessedText, { async: false }) as string
      }
    } catch {
      html = escapeHtml(preprocessedText)
    }

    // 10. Restore protected math tokens
    mathTokens.forEach((rendered, i) => {
      html = html.split(`KATEXDISPTOKEN${i}END`).join(rendered)
      html = html.split(`KATEXINLINETOKEN${i}END`).join(rendered)
    })

    // 10.5 Restore protected SVG tokens (removing any paragraph wrapping introduced by marked)
    svgTokens.forEach((rendered, i) => {
      const token = `PREPKIYASVGTOKEN${i}END`
      const pWrappedRegex = new RegExp(`<p>\\s*${token}\\s*<\\/p>`, 'g')
      html = html.replace(pWrappedRegex, rendered)
      html = html.split(token).join(rendered)
    })

    // 11. Sanitize HTML
    return DOMPurify.sanitize(html, DOMPURIFY_CONFIG)
  }, [rawText, format, inline])

  const Component = inline ? 'span' : 'div'
  const baseClass = inline ? 'inline-block' : 'markdown-content'

  return (
    <Component
      className={`${baseClass} ${className}`.trim()}
      dangerouslySetInnerHTML={{ __html: renderedHtml }}
    />
  )
}
